import { useRef, useState, type DragEvent } from 'react';
import { Button, Spinner, makeStyles, mergeClasses } from '@fluentui/react-components';
import { Add20Regular, ArrowUpload24Regular, Delete16Regular } from '@fluentui/react-icons';
import {
  esAceptado,
  estadoDelPlano,
  etiquetaRolArchivo,
  etiquetaTipoDePlano,
  motivoDeRechazo,
  tipoDePlanoPorNombre,
  type EstadoDelPlano,
} from '@supernova/shared';
import type { PlanoGaleriaItem } from '../../api/planos.js';
import { DWG_DXF_ENABLED } from '../../lib/flags.js';
import { MiniaturaPlano } from './MiniaturaPlano.js';
import { PreguntaUnidad } from './PreguntaUnidad.js';
import { planoPrincipal } from './usePlanosGaleria.js';

/**
 * GALERÍA DE PLANOS del proyecto (OPUS ci): una ficha por plano. Cada ficha contesta tres cosas:
 * qué es (miniatura y tipo), para qué sirve (su papel en la hoja) y cómo va (sin empezar, cuántos
 * renglones lleva o listo). Tocarla abre el plano. La última ficha es para subir otro.
 */

/** Lo que el selector de archivos ofrece en la galería: solo planos. */
const ACEPTA_PLANOS = '.ifc,.dwg,.dxf,.pdf';

function plural(n: number): string {
  return `${n} ${n === 1 ? 'renglón' : 'renglones'}`;
}

const AVISO_PDF = 'De un PDF solo copio lo que viene escrito (cotas, tablas, notas): no mido el dibujo. Revisa las cantidades.';
const AVISO_DWG_SIN_MEDIR = 'Casi no encontré qué medir en este plano. Te puede servir de consulta.';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
    gap: '16px',
  },
  ficha: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'left',
    padding: 0,
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-lg)',
    background: 'var(--bg-surface)',
    color: 'var(--text-primary)',
    overflow: 'hidden',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transitionProperty: 'border-color, box-shadow, transform',
    transitionDuration: '140ms',
    ':hover': {
      borderTopColor: 'var(--accent)',
      borderRightColor: 'var(--accent)',
      borderBottomColor: 'var(--accent)',
      borderLeftColor: 'var(--accent)',
      boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
    },
    ':focus-visible': { outline: '2px solid var(--accent)', outlineOffset: '2px' },
  },
  fichaTrabajada: {
    borderTopColor: 'var(--accent)',
    borderRightColor: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
    borderLeftColor: 'var(--accent)',
    boxShadow: '0 0 0 1px var(--accent)',
  },
  trabajado: {
    position: 'absolute',
    left: '8px',
    bottom: '8px',
    padding: '2px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 700,
    background: 'var(--accent)',
    color: '#fff',
  },
  guia: {
    padding: '10px 14px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-surface)',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    lineHeight: 1.5,
  },
  miniWrap: { position: 'relative' },
  tipo: {
    position: 'absolute',
    left: '8px',
    top: '8px',
    padding: '2px 8px',
    borderRadius: '6px',
    fontFamily: 'var(--font-mono)',
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '0.05em',
    background: 'var(--bg-deep)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border-subtle)',
  },
  borrar: {
    position: 'absolute',
    right: '8px',
    top: '8px',
    width: '28px',
    height: '28px',
    display: 'grid',
    placeItems: 'center',
    borderRadius: '6px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-deep)',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    ':hover': { color: 'var(--bad)' },
  },
  cuerpo: { display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px 14px 14px' },
  nombre: {
    fontWeight: 600,
    fontSize: '14px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '22px',
    padding: '0 9px',
    borderRadius: '999px',
    border: '1px solid var(--border-default)',
    fontSize: '11.5px',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  punto: { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  datos: { fontSize: '12px', color: 'var(--text-tertiary)' },
  aviso: {
    fontSize: '12px',
    lineHeight: 1.45,
    padding: '7px 9px',
    borderRadius: '8px',
    background: 'var(--warn-weak)',
    color: 'var(--warn)',
  },
  subir: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minHeight: '220px',
    padding: '16px',
    border: '2px dashed var(--border-default)',
    borderRadius: 'var(--radius-lg)',
    background: 'transparent',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    textAlign: 'center',
    ':hover': { borderTopColor: 'var(--accent)', borderRightColor: 'var(--accent)', borderBottomColor: 'var(--accent)', borderLeftColor: 'var(--accent)', color: 'var(--text-primary)' },
  },
  subirActivo: { borderTopColor: 'var(--accent)', borderRightColor: 'var(--accent)', borderBottomColor: 'var(--accent)', borderLeftColor: 'var(--accent)', background: 'var(--accent-weak)' },
  mas: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    background: 'var(--bg-raised)',
    color: 'var(--accent)',
  },
  vacio: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    padding: '36px 24px',
    border: '2px dashed var(--border-default)',
    borderRadius: 'var(--radius-xl)',
    background: 'var(--bg-surface)',
    textAlign: 'center',
  },
  vacioTitulo: { fontSize: '18px', fontWeight: 700, margin: 0 },
  vacioSub: { fontSize: '13.5px', color: 'var(--text-secondary)', margin: 0, maxWidth: '520px' },
  formatos: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    width: '100%',
    maxWidth: '760px',
    textAlign: 'left',
  },
  formato: {
    padding: '12px 14px',
    borderRadius: '10px',
    border: '1px solid var(--border-subtle)',
    background: 'var(--bg-page)',
    fontSize: '13px',
    lineHeight: 1.45,
    color: 'var(--text-secondary)',
  },
  formatoTitulo: { display: 'block', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '2px' },
  nota: { fontSize: '12.5px', color: 'var(--text-tertiary)', margin: 0 },
  rechazo: { fontSize: '12.5px', color: 'var(--warn)', margin: 0 },
});

function colorDeEstado(e: EstadoDelPlano): string {
  if (e === 'listo') return 'var(--ok)';
  if (e === 'en_curso') return 'var(--warn)';
  return 'var(--text-tertiary)';
}

interface Props {
  planos: PlanoGaleriaItem[] | null;
  canMutate: boolean;
  subiendo: boolean;
  progreso?: number;
  onSubir: (files: File[]) => Promise<void>;
  onAbrir: (fileId: string) => void;
  /** Quitar un plano del proyecto (pide confirmación el llamador). Sin esto, no hay botón. */
  onEliminar?: (fileId: string) => void;
  /** No se pudo cargar la lista (tras reintentar): se ofrece reintentar, no "no hay planos". */
  error?: boolean;
  onReintentar?: () => void;
  /** Contestar en qué unidad está dibujado un plano con escala dudosa. Sin esto, no se pregunta aquí. */
  onElegirUnidad?: (fileId: string, unidad: string) => Promise<void>;
}

export function PlanosGaleria({ planos, canMutate, subiendo, progreso, onSubir, onAbrir, onEliminar, error, onReintentar, onElegirUnidad }: Props) {
  const styles = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const [rechazados, setRechazados] = useState<string[]>([]);
  const aceptacion = { dwgDxf: DWG_DXF_ENABLED };

  async function recibir(list: FileList | null) {
    if (!list || list.length === 0 || subiendo) return;
    const todos = Array.from(list);
    // En la galería solo entran planos; lo demás va a "Documentos de apoyo".
    const ok = todos.filter((f) => tipoDePlanoPorNombre(f.name) != null && esAceptado(f.name, aceptacion));
    const malos = todos.filter((f) => !ok.includes(f));
    setRechazados(malos.map((f) => f.name));
    if (ok.length > 0) await onSubir(ok);
  }

  const zonaProps = canMutate
    ? {
        onDragOver: (e: DragEvent) => {
          e.preventDefault();
          setArrastrando(true);
        },
        onDragLeave: () => setArrastrando(false),
        onDrop: (e: DragEvent) => {
          e.preventDefault();
          setArrastrando(false);
          void recibir(e.dataTransfer.files);
        },
      }
    : {};

  const input = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={ACEPTA_PLANOS}
      style={{ display: 'none' }}
      onChange={(e) => {
        void recibir(e.target.files);
        if (inputRef.current) inputRef.current.value = '';
      }}
    />
  );

  const avisoRechazo =
    rechazados.length > 0 ? (
      <p className={styles.rechazo}>
        {rechazados.some((n) => tipoDePlanoPorNombre(n) == null)
          ? `Aquí solo van planos (IFC, DWG, DXF o PDF). ${rechazados.join(', ')} no es un plano: súbelo en «Documentos de apoyo».`
          : motivoDeRechazo(rechazados, aceptacion)}
      </p>
    ) : null;

  if (planos === null && error) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, color: 'var(--text-secondary)' }}>
        No pude cargar los planos.
        {onReintentar && (
          <Button size="small" appearance="primary" onClick={onReintentar}>
            Reintentar
          </Button>
        )}
      </div>
    );
  }
  if (planos === null) {
    return <Spinner size="small" label="Cargando planos…" />;
  }

  if (planos.length === 0) {
    return (
      <div className={mergeClasses(styles.vacio, arrastrando && styles.subirActivo)} {...zonaProps}>
        <div className={styles.mas}>{subiendo ? <Spinner size="tiny" /> : <ArrowUpload24Regular />}</div>
        <h3 className={styles.vacioTitulo}>Sube los planos de tu obra</h3>
        <p className={styles.vacioSub}>
          {canMutate
            ? 'Arrástralos aquí o elígelos de tu computadora. Con ellos mido la obra y armamos juntos la hoja de presupuesto.'
            : 'Este proyecto todavía no tiene planos.'}
        </p>
        <div className={styles.formatos}>
          <div className={styles.formato}>
            <span className={styles.formatoTitulo}>Modelo 3D (IFC)</span>
            El mejor: el modelo ya trae sus medidas y yo solo las leo.
          </div>
          <div className={styles.formato}>
            <span className={styles.formatoTitulo}>Dibujo (DWG o DXF)</span>
            Mido sobre el dibujo y te pregunto lo que no se dibuja, como la altura.
          </div>
          <div className={styles.formato}>
            <span className={styles.formatoTitulo}>PDF</span>
            Solo copio lo que viene escrito (cotas, tablas, notas); el dibujo no lo mido. Las cantidades salen aproximadas.
          </div>
        </div>
        <p className={styles.nota}>
          Una foto de un plano no sirve. Si solo lo tienes en papel, escanéalo como PDF (solo copiaré lo escrito).
        </p>
        {canMutate && (
          <Button appearance="primary" disabled={subiendo} onClick={() => inputRef.current?.click()}>
            {subiendo ? `Subiendo ${Math.round((progreso ?? 0) * 100)}%…` : 'Elegir archivos'}
          </Button>
        )}
        {avisoRechazo}
        {input}
      </div>
    );
  }

  // Con varios planos, que se vea en cuál ya se trabajó (el que más renglones aporta a la hoja).
  const trabajado = planos.length > 1 ? planoPrincipal(planos) : null;
  const idTrabajado = trabajado && trabajado.renglones > 0 ? trabajado.fileId : null;
  const hayReferencia = planos.some((p) => p.rol === 'referencia_spec');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {planos.length > 1 && (
        <div className={styles.guia}>
          {idTrabajado && trabajado
            ? `Tienes ${planos.length} planos. Ya trabajaste en «${trabajado.nombre}» (${plural(trabajado.renglones)}): empieza por ahí. `
            : `Tienes ${planos.length} planos y todavía no empiezas ninguno: abre el que quieras presupuestar. `}
          {'La hoja es una sola para todo el proyecto y junta lo que midas de cada plano.'}
          {hayReferencia ? ' Los que dicen «Solo referencia» sirven de consulta y no suman cantidades.' : ''}
        </div>
      )}
      <div className={styles.grid}>
        {planos.map((p) => {
          const est = estadoDelPlano(p.renglones, p.avance);
          const esTrabajado = p.fileId === idTrabajado;
          return (
            <div
              key={p.fileId}
              role="button"
              tabIndex={0}
              className={mergeClasses(styles.ficha, esTrabajado && styles.fichaTrabajada)}
              onClick={() => onAbrir(p.fileId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onAbrir(p.fileId);
                }
              }}
              title={`Abrir ${p.nombre}`}
            >
              <div className={styles.miniWrap}>
                <MiniaturaPlano plano={p} />
                <span className={styles.tipo}>{etiquetaTipoDePlano(p.tipo, p.nombre)}</span>
                {esTrabajado && <span className={styles.trabajado}>Aquí ya trabajaste</span>}
                {onEliminar && (
                  <button
                    type="button"
                    className={styles.borrar}
                    aria-label={`Quitar ${p.nombre} del proyecto`}
                    title="Quitar este plano del proyecto"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEliminar(p.fileId);
                    }}
                  >
                    <Delete16Regular />
                  </button>
                )}
              </div>
              <div className={styles.cuerpo}>
                <div className={styles.nombre}>{p.nombre}</div>
                <div className={styles.chips}>
                  {p.rol && <span className={styles.chip}>{etiquetaRolArchivo(p.rol)}</span>}
                  <span className={styles.chip}>
                    <span className={styles.punto} style={{ background: colorDeEstado(est.estado) }} />
                    {est.texto}
                  </span>
                </div>
                {p.datos && <div className={styles.datos}>{p.datos}</div>}
                {p.tipo === 'pdf' && <div className={styles.aviso}>{AVISO_PDF}</div>}
                {p.sinNadaMedible && <div className={styles.aviso}>{AVISO_DWG_SIN_MEDIR}</div>}
                {p.preguntarUnidad && p.escala && onElegirUnidad && canMutate && (
                  <PreguntaUnidad compacta escala={p.escala} onElegir={(u) => onElegirUnidad(p.fileId, u)} />
                )}
              </div>
            </div>
          );
        })}
        {canMutate && (
          <button
            type="button"
            className={mergeClasses(styles.subir, arrastrando && styles.subirActivo)}
            onClick={() => !subiendo && inputRef.current?.click()}
            {...zonaProps}
          >
            <span className={styles.mas}>{subiendo ? <Spinner size="tiny" /> : <Add20Regular />}</span>
            <span style={{ fontWeight: 600 }}>
              {subiendo ? `Subiendo ${Math.round((progreso ?? 0) * 100)}%…` : 'Sube otro plano'}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>IFC, DWG, DXF o PDF. Arrástralo aquí.</span>
          </button>
        )}
      </div>
      {avisoRechazo}
      {input}
    </div>
  );
}
